import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BiometricService } from '../biometric.service';
import { LockStateService } from '../lock-state.service';
import { AuthenticationService } from '../../../security/auth/authentication.service';

/**
 * Biometric lock screen shown on native cold start when a stored session needs
 * to be unlocked. Prompts for Face ID / fingerprint; on success routes to the
 * originally requested URL, on failure offers a retry or a password sign-in.
 */
@Component({
  selector: 'app-lock',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <div class="lock">
      <mat-icon class="lock__icon">lock</mat-icon>
      <h2 class="lock__title">Tourist Tax</h2>
      <p class="lock__msg">{{ message }}</p>

      <mat-spinner *ngIf="checking" diameter="36"></mat-spinner>

      <div class="lock__actions" *ngIf="!checking">
        <button mat-raised-button color="primary" (click)="unlock()">Unlock</button>
        <button mat-button (click)="usePassword()">Sign in with password</button>
      </div>
    </div>
  `,
  styles: [`
    .lock { min-height: 100vh; display: flex; flex-direction: column; align-items: center;
            justify-content: center; gap: 16px; padding: 24px; text-align: center; }
    .lock__icon { font-size: 56px; height: 56px; width: 56px; opacity: .8; }
    .lock__title { margin: 0; }
    .lock__msg { margin: 0; color: rgba(0,0,0,.6); }
    .lock__actions { display: flex; flex-direction: column; gap: 8px; width: 100%; max-width: 280px; }
  `],
})
export class LockComponent implements OnInit {
  checking = true;
  message = 'Unlock to continue';
  private returnUrl = '/app';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly biometric: BiometricService,
    private readonly lockState: LockStateService,
    private readonly auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/app';
    this.unlock();
  }

  async unlock(): Promise<void> {
    this.checking = true;
    this.message = 'Unlock to continue';
    const ok = await this.biometric.authenticate('Unlock Tourist Tax');
    if (ok) {
      this.lockState.markUnlocked();
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    this.checking = false;
    this.message = 'Authentication failed. Try again or sign in with your password.';
  }

  usePassword(): void {
    // Drop the stored session and fall back to the normal login screen.
    this.auth.logout();
    this.lockState.reset();
    this.router.navigate(['/login']);
  }
}
